/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, ComponentType, useCallback, useMemo } from 'react';
import Paper from '@mui/material/Paper';
import {
  Grid,
  TableColumnVisibility,
  Table as DevExpressTable,
  TableHeaderRow,
  PagingPanel,
  ColumnChooser,
  SearchPanel,
  Toolbar,
  TableEditRow,
  TableEditColumn,
  TableFilterRow,
  TableGroupRow,
  TableColumnResizing,
  ExportPanel,
} from '@devexpress/dx-react-grid-material-ui';
import {
  SortingState,
  IntegratedSorting,
  PagingState,
  IntegratedPaging,
  FilteringState,
  IntegratedFiltering,
  SearchState,
  Sorting,
  EditingState,
  ChangeSet,
  GroupingState,
  IntegratedGrouping,
  CustomPaging,
  TableFilterRow as TableFilterRowBase,
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import { Box, Button, IconButton } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import AddButton from '../../pages/Options/BrachofficePage/components/AddButton';
import EditButton from '../../pages/Options/BrachofficePage/components/EditButton';
import DeleteButton from '../../pages/Options/BrachofficePage/components/DeleteButton';
import CommitButton from '../../pages/Options/BrachofficePage/components/CommitButton';
import { Service } from '../../types/Service.types';
import RowsProvider from '../../pages/AgentTable/components/RowsProvider';
import { DEFAULTTABLEPAGESIZE } from '../../services/config/CONST';

function ToolbarRoot({ ...restProps }) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Toolbar.Root {...restProps} />;
}

interface CancelButtonProps {
  onExecute: () => void;
}
function CancelButton({ onExecute }: CancelButtonProps) {
  return (
    <IconButton color="secondary" onClick={onExecute} title="Änderungen verwerfen">
      <CancelIcon />
    </IconButton>
  );
}

// TODO What the code in the commend do

// const global = { title: ['Yes', 'No'] };

// const availableValues = {
//   is_default: global.title,
//   show_in_export_xml: global.title,
// };

export const filterRowMessages: TableFilterRowBase.LocalizationMessages = {
  filterPlaceholder: 'Filter...',
  contains: 'Enthält',
  notContains: 'Enthält nicht',
  startsWith: 'Beginnt mit',
  endsWith: 'Endet mit',
  equal: 'Gleich',
  notEqual: 'Ungleich',
  greaterThan: 'Größer als',
  greaterThanOrEqual: 'Größer oder gleich',
  lessThan: 'Kleiner als',
  lessThanOrEqual: 'Kleiner oder gleich',
};

function EditCell(props: TableEditRow.CellProps) {
  // const { column, value, editingEnabled, row } = props;
  // TODO What the code in the commend do

  // @ts-ignore
  // const availableColumnValues = availableValues[column?.name];
  // if (availableColumnValues) {
  //   return <LookupEditCell {...props} availableColumnValues={availableColumnValues} />;
  // }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <TableEditRow.Cell {...props} />;
}

// CONTRACT: Jede row MUSS ein valides id-Feld haben (string|number, kein undefined/NaN).
// CONTRACT: Platzhalter-Rows (z.B. PrmUserInit) verwenden id = 0 als Sentinel.
// CONTRACT: Der ?? 0-Fallback ist letzte Verteidigung gegen fehlende id-Felder — strukturelle Garantie ist PrmUserInit.id = 0.
// CONTRACT: Exportiert fuer Unit-/E2E-Tests — Aenderungen hier brechen Vertrags-Assertions in user-password-hash-removal.spec.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getRowId = (row: any): string | number => row.id ?? 0;

interface Props {
  row: any[];
  columns: any[];
  pageSize?: number;
  pageSizes?: number[];
  defaultHiddenColumnNames?: string[];
  defaultColumnWidths?: any[];
  onCommitChanges?: (changes: ChangeSet) => void;
  columnExtensions?: EditingState.ColumnExtension[];
  dataTypeProvider?: (() => JSX.Element)[];
  // TODO implement commandComponent to Table
  // commandComponent?: ComponentType<TableEditColumn.CommandProps>;
  EditCellForCommand?: ComponentType<TableEditColumn.CellProps>;
  rowComponent?: ComponentType<DevExpressTable.DataRowProps>;
  FilterRow?: boolean;
  editable?: boolean;
  showAddCommand?: boolean;
  showEditCommand?: boolean;
  showDeleteCommand?: boolean;
  groupingTable?: boolean;
  groupingCollumName?: any[];
  filteringState?: any[];
  sortingProp?: any[];
  forServicesById?: { tableName: string; services: Service[]; agent_id: string | undefined; refresh: () => void };
  exporting?: {
    all?: {
      export: boolean;
      refConst: any | null;
      rows: any[];
      columns: any[];
      onSave: (wokbook: any) => void;
      exportFunk: () => any;
    };
    filter?: {
      export: boolean;
      refConst: any | null;
      columns: any[];
      onSave: (wokbook: any) => void;
      exportFunk: () => any;
    };
  };
  TableCommandComponents?: any;
  // Server-side props
  totalCount?: number;
  currentPage?: number;
  onCurrentPageChange?: (page: number) => void;
  externalSorting?: Sorting[];
  onSortingChange?: (sorting: Sorting[]) => void;
  externalFiltering?: any[];
  onFiltersChange?: (filters: any[]) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  FilterCell?: ComponentType<TableFilterRow.CellProps>;
}

export const CommandComponents = {
  add: AddButton,
  edit: EditButton,
  delete: DeleteButton,
  commit: CommitButton,
  cancel: CancelButton,
};

function Table({
  row,
  columns,
  pageSize = DEFAULTTABLEPAGESIZE.pageSize,
  pageSizes = [5, 10, 15, 20, 0],
  defaultHiddenColumnNames = [],
  defaultColumnWidths = [],
  onCommitChanges,
  columnExtensions,
  dataTypeProvider = [],
  FilterRow = false,
  editable = false,
  showAddCommand = false,
  showEditCommand = false,
  showDeleteCommand = false,
  EditCellForCommand,
  groupingTable = false,
  groupingCollumName,
  forServicesById,
  filteringState = [],
  sortingProp,
  rowComponent,
  exporting,
  TableCommandComponents,
  totalCount,
  currentPage,
  onCurrentPageChange,
  externalSorting,
  onSortingChange,
  externalFiltering,
  onFiltersChange,
  searchValue,
  onSearchChange,
  FilterCell,
}: Props): JSX.Element {
  // TODO implement sorting to Table
  const [internalSorting, setInternalSorting] = useState<Sorting[]>([{ columnName: 'ID', direction: 'asc' }]);
  const [internalCurrentPage, setInternalCurrentPage] = useState<number>(0);
  const [pageSizeState, setPageSize] = useState<number>(pageSize);
  const [exportedRows, setExportedRows] = useState([]);

  useEffect(() => {
    setPageSize(pageSize);
  }, [pageSize]);

  const handleSorting = (newSorting: Sorting[]) => {
    if (onSortingChange) {
      onSortingChange(newSorting);
    } else {
      setInternalSorting(newSorting);
    }
  };

  const finalSorting = externalSorting || internalSorting;
  const finalCurrentPage = currentPage !== undefined ? currentPage : internalCurrentPage;
  const finalOnCurrentPageChange = onCurrentPageChange || setInternalCurrentPage;
  // const finalFiltering = externalFiltering || filteringState;

  // Use Integrated plugins only if totalCount is NOT provided (Client-Side Mode)
  const isServerSide = totalCount !== undefined;

  const Command = useCallback(({ id, onExecute }: TableEditColumn.CommandProps) => {
    const CommandButton = TableCommandComponents ? TableCommandComponents[id] : CommandComponents[id];
    return <CommandButton onExecute={onExecute} forServicesById={forServicesById} />;
  }, []);

  const DefaultRow = useCallback(
    ({ row: dataRow, children, tableRow }: DevExpressTable.DataRowProps) => (
      <DevExpressTable.Row
        key={Math.random()}
        tableRow={tableRow}
        row={dataRow}
        // eslint-disable-next-line react/no-children-prop
        children={children}
      />
    ),
    [],
  );

  const exportMenu = useCallback(
    () => (
      <Box display="flex" flexDirection="column">
        {exporting?.all?.export && <Button onClick={exporting?.all?.exportFunk}>Alle</Button>}
        {exporting?.filter?.export && <Button onClick={exporting?.filter?.exportFunk}>Filter</Button>}
      </Box>
    ),
    [],
  );
  const [columnWidths, setColumnWidths] = useState(
    defaultColumnWidths.length > 0 ? defaultColumnWidths : columns.map((col) => ({ columnName: col.name, width: 180 })),
  );
  const [resizingMode] = useState('nextColumn');
  const [hiddenColumnNames, setHiddenColumnNames] = useState([]);

  // Memoize visibleColumns to prevent unnecessary re-renders
  const visibleColumns = useMemo(
    () =>
      /* @ts-ignore */
      columns.filter((column) => !hiddenColumnNames.includes(column.name)),
    [columns, hiddenColumnNames],
  );

  return (
    // For the correct display of the tables, the position of the table extensions must be observed
    <Paper>
      <Grid rows={row} columns={columns} getRowId={getRowId}>
        <SearchState defaultValue="" value={searchValue} onValueChange={onSearchChange} />
        <FilteringState defaultFilters={filteringState} filters={externalFiltering} onFiltersChange={onFiltersChange} />
        <SortingState
          defaultSorting={sortingProp || [{ columnName: 'exported_at', direction: 'desc' }]}
          sorting={finalSorting}
          onSortingChange={handleSorting}
        />
        {groupingTable && <GroupingState grouping={groupingCollumName} />}
        {groupingTable && <IntegratedGrouping />}
        <PagingState
          currentPage={finalCurrentPage}
          onCurrentPageChange={finalOnCurrentPageChange}
          pageSize={pageSizeState}
          onPageSizeChange={setPageSize}
        />
        {isServerSide ? <CustomPaging totalCount={totalCount} /> : <IntegratedPaging />}
        {!isServerSide && <IntegratedFiltering />}
        {!isServerSide && <IntegratedSorting />}
        <RowsProvider onRowsUpdate={setExportedRows} />
        <EditingState onCommitChanges={onCommitChanges || (() => {})} columnExtensions={columnExtensions} />
        <DevExpressTable rowComponent={rowComponent || DefaultRow} messages={{ noData: 'Keine Daten' }} />
        <TableColumnResizing
          columnWidths={columnWidths}
          onColumnWidthsChange={setColumnWidths}
          resizingMode={resizingMode}
        />

        <TableHeaderRow showSortingControls messages={{ sortingHint: 'Sortieren' }} />
        <TableColumnVisibility
          defaultHiddenColumnNames={defaultHiddenColumnNames}
          hiddenColumnNames={hiddenColumnNames}
          /* @ts-ignore */
          onHiddenColumnNamesChange={setHiddenColumnNames}
        />
        {dataTypeProvider.map((item) => item())}
        {FilterRow && (
          <TableFilterRow messages={filterRowMessages} {...(FilterCell ? { cellComponent: FilterCell } : {})} />
        )}
        <Toolbar rootComponent={ToolbarRoot} />
        <SearchPanel messages={{ searchPlaceholder: 'Suchen...' }} />
        <ColumnChooser messages={{ showColumnChooser: 'Spaltenauswahl anzeigen' }} />
        <PagingPanel
          pageSizes={pageSizes}
          messages={{
            showAll: 'Alle',
            rowsPerPage: 'Zeilen pro Seite',
            info: ({ from, to, count }) => `${from}–${to} von ${count}`,
          }}
        />
        {editable && <TableEditRow cellComponent={EditCell} />}
        {editable && (
          <TableEditColumn
            // eslint-disable-next-line react/jsx-props-no-spreading
            cellComponent={EditCellForCommand || TableEditColumn.Cell}
            showAddCommand={showAddCommand}
            showEditCommand={showEditCommand}
            showDeleteCommand={showDeleteCommand}
            commandComponent={Command}
          />
        )}
        {groupingTable && <TableGroupRow />}
        {/* {exporting?.filter?.export && <ExportPanel menuItemComponent={exportMenu} />} */}
        {exporting?.all?.export && <ExportPanel menuItemComponent={exportMenu} />}
      </Grid>
      {exporting?.all?.export && (
        <GridExporter
          ref={exporting.all.refConst}
          rows={exporting.all.rows}
          columns={visibleColumns}
          onSave={exporting.all.onSave}
        />
      )}
      {exporting?.filter?.export && (
        <GridExporter
          ref={exporting.filter.refConst}
          rows={exportedRows}
          columns={visibleColumns}
          onSave={exporting.filter.onSave}
        />
      )}
    </Paper>
  );
}

export default Table;
